import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Boxes, Download, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { exportInvStock } from "~/services/inventory/exportService";
import {
  InvSourceKind,
  InvStockRow,
  inventoryStockService,
} from "~/services/inventory/stockService";
import { formatCurrency } from "~/utils/helpers";

const KIND_LABELS: Record<InvSourceKind, string> = {
  finished_good: "Producto Terminado",
  raw_material: "Materia Prima",
  oil: "Aceite",
};

const formatQty = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n ?? 0);

export function InventoryStockPage() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["inv-stock", search],
    queryFn: () => inventoryStockService.getStock(search),
  });

  const rows = useMemo<InvStockRow[]>(() => data ?? [], [data]);

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. No
          reemplaza al inventario actual; el saldo se calcula como la suma de los movimientos.
        </span>
      </div>

      <PageHeader
        title="Inventario — Stock"
        description="Saldos por ubicación en el nuevo modelo de movimientos."
        actions={
          <Button
            variant="outline"
            className="h-9"
            disabled={rows.length === 0}
            onClick={() => exportInvStock(rows)}
          >
            <Download className="mr-1.5 h-4 w-4" />
            Exportar Excel
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre…"
            className="h-9 pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={8} columns={7} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="Sin saldos"
          description="No hay existencias que coincidan con la búsqueda en el nuevo inventario."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>UM</TableHead>
                    <TableHead className="text-right">Disponible</TableHead>
                    <TableHead className="text-right">Valor (CLP)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={`${r.product_id}-${r.location_id}`}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.code ?? "-"}
                      </TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-normal">
                          {KIND_LABELS[r.source_kind]}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.location_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQty(r.quantity)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{r.stock_uom}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQty(r.available)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.value == null ? "-" : `$${formatCurrency(r.value)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default InventoryStockPage;
